import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const bugReportingGuide: AdminGuide = {
  id: "bug-reporting",
  title: "Submitting a bug report (Admin Bug Tracker → monday)",
  category: "Bug Reporting",
  audience: ["Support", "Ops", "Finance", "CS"],
  summary:
    "File a high-quality bug report through Admin so it can be validated quickly and tracked in monday with a stable deep link.",
  keywords: [
    "bug",
    "bug tracker",
    "monday",
    "report",
    "preview",
    "steps to reproduce",
    "screenshot",
    "expected vs actual",
    "correlation id",
  ],
  location: "Admin",
  risk: "medium",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.BUG_TRACKER_FEATURE,
    GUIDE_SOURCE_DOCS.ADMIN_BUG_TRACKER,
  ],
  warnings: [
    "Do not paste secrets, tokens, passwords, or personal data into bug reports.",
    "If your URL contains credentials (for example `token=`), do not paste it. Remove the token or ask Engineering for a safe alternative.",
    "Avoid screenshots with sensitive financial data. Crop to the minimum area needed.",
  ],
  whatThisHelpsYouDo: [
    "Create a reproducible record of the issue with a stable admin link.",
    "Get fast validation feedback (quality notes + follow-up questions).",
    "Ensure the issue is tracked in monday with the correct deep link and IDs.",
  ],
  whenToUse: [
    "A customer or internal tester reports something that looks wrong in the product.",
    "You can reproduce the issue on Preview or Production.",
    "You need to hand off to Engineering with clear, structured evidence.",
  ],
  whenNotToUse: [
    "You are unsure whether it’s a bug vs expected behavior (ask for clarification first).",
    "You cannot safely describe the issue without exposing sensitive data (escalate and ask for guidance).",
  ],
  beforeYouStart: [
    "The exact page URL where you saw the issue (prefer Preview when appropriate).",
    "Which organisation you were using (org name and, if available, organisationId).",
    "If user-specific: the user email and/or userId/UID.",
    "A short description of what you did and what you expected.",
    "Optional: a cropped screenshot that does not include secrets/PII.",
  ],
  steps: [
    "Reproduce the issue once, then refresh and reproduce again to confirm it is consistent.",
    "Copy the page URL from the browser address bar (do not hand-type it).",
    "Open Admin → **Bug Tracker** (`/bug-tracker`).",
    "Select the organisation (use a real org for real issues; use `[DEMO MODE]` orgs only for demo-mode issues).",
    "Paste the page URL. Confirm it does not include secrets (no tokenised URLs).",
    "Fill **Affected heading** with the smallest section name that helps someone find the issue fast.",
    "Write the description using: what I did → what I expected → what happened → how often.",
    "Optional: attach a screenshot (crop to the minimum).",
    "Click **Validate** and review the validation notes/follow-up questions.",
    "Answer follow-ups (short, factual answers). Add one “additional context” note only if needed.",
    "Click **Approve & Submit**. Copy the final admin link for reference.",
  ],
  checklists: [
    {
      title: "Bug report quality checklist",
      items: [
        "Clear steps (someone else can follow them).",
        "Expected vs actual behavior is explicit.",
        "Includes environment (Preview vs Production) and the time it occurred.",
        "Includes org/user identifiers when relevant (IDs, not personal data).",
        "No secrets, tokenised URLs, or unsafe screenshots.",
      ],
    },
    {
      title: "Required info checklist (quick)",
      items: [
        "Organisation name (and organisationId if available).",
        "User email/UID (if the bug is user-specific).",
        "Timestamp (include timezone).",
        "Steps to reproduce (numbered).",
        "Expected vs actual.",
        "Screenshot (cropped) if helpful and safe.",
      ],
    },
  ],
  whatSuccessLooksLike: [
    "The Bug Tracker page shows the report as submitted/processing (phase 2 runs in the background).",
    "You have a stable admin deep link for the report (full UUID).",
    "Engineering can reproduce without immediate follow-up.",
  ],
  troubleshooting: [
    {
      title: "Validate fails or never finishes",
      body: [
        "Refresh the page once and click **Validate** again (it creates a new draft).",
        "If it still fails, escalate with the time you tried, the org/user identifiers, and what you saw.",
      ],
    },
    {
      title: "Collecting a correlation ID (optional, if Engineering asks)",
      body: [
        "In the browser devtools Network tab, select the relevant API call and look for `X-Correlation-ID` in response headers.",
        "Copy the correlation ID only (do not copy request/response bodies that may contain sensitive data).",
      ],
    },
  ],
  escalateWhen: [
    "You cannot safely include necessary context without risking sensitive data exposure.",
    "Validate repeatedly fails or the report appears stuck.",
    "The issue involves billing/provider connections or other high-risk operations.",
  ],
  relatedGuides: [
    { label: "Spoofing safely", guideId: "spoofing-safely" },
    {
      label: "Investigate a submitted bug report",
      guideId: "bug-report-investigation",
    },
  ],
  relatedAdminPaths: [
    { label: "Bug Tracker", path: "/bug-tracker" },
    { label: "Bug Reports (recent)", path: "/bug-tracker/recent" },
  ],
};
